// GlobalConfirmModal — wraps DS `Dialog` for confirmation prompts (US-051).
//
// DS-GAP-DESIGN (US-051):
//   The spec asks for a Promise-returning `confirm()` API any feature can call.
//   We mirror the pattern the DS `Toaster` uses: a module-scoped event bus
//   serialises calls into a single mounted `<GlobalConfirmModalRoot>`, so
//   feature pages don't have to thread state for one-off confirms. Two
//   tones — `default` and `destructive` (red Confirm CTA per US-051).
//
//   Returns `Promise<boolean>` resolved on accept (true) / decline (false /
//   ESC / outside-click).

import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import { useT } from '@barel/i18n';
import {
  Button,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
} from '@barel/ui';

export type ConfirmTone = 'default' | 'destructive';

export interface ConfirmOptions {
  /** Pre-localised modal title. Falls back to `confirmModal.defaultTitle`. */
  title?: ReactNode;
  /** Pre-localised body. Falls back to `confirmModal.defaultBody`. */
  body?: ReactNode;
  /** Pre-localised confirm CTA. Defaults per tone. */
  confirmLabel?: ReactNode;
  /** Pre-localised cancel CTA. Defaults to the common `cancel` key. */
  cancelLabel?: ReactNode;
  /** Tone — destructive flips the Confirm button to the danger variant. */
  tone?: ConfirmTone;
}

interface QueueEntry extends ConfirmOptions {
  id: string;
  resolve: (accepted: boolean) => void;
}

type Listener = (entry: QueueEntry | null) => void;

interface ConfirmBus {
  subscribe(fn: Listener): () => void;
  getSnapshot(): QueueEntry | null;
  push(opts: ConfirmOptions): Promise<boolean>;
  resolveCurrent(accepted: boolean): void;
}

function createBus(): ConfirmBus {
  let current: QueueEntry | null = null;
  const queue: QueueEntry[] = [];
  const listeners = new Set<Listener>();
  let counter = 0;

  function emit() {
    for (const fn of listeners) fn(current);
  }

  function next() {
    current = queue.shift() ?? null;
    emit();
  }

  return {
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    getSnapshot() {
      return current;
    },
    push(opts) {
      counter += 1;
      const id = `confirm-${counter}-${Date.now().toString(36)}`;
      return new Promise<boolean>((resolve) => {
        const entry: QueueEntry = { ...opts, id, resolve };
        if (current === null) {
          current = entry;
          emit();
        } else {
          queue.push(entry);
        }
      });
    },
    resolveCurrent(accepted) {
      if (!current) return;
      current.resolve(accepted);
      next();
    },
  };
}

const bus: ConfirmBus = createBus();

/** Imperative API — `confirm({ title, body, tone })` returns Promise<boolean>. */
export function confirm(opts: ConfirmOptions = {}): Promise<boolean> {
  return bus.push(opts);
}

/**
 * Global confirm-modal mount. Mount once at the App root (alongside the
 * Toaster). Subscribes to the bus and renders one Dialog per pending entry.
 */
export function GlobalConfirmModal() {
  const t = useT('shell');
  const tCommon = useT('common');
  const entry = useSyncExternalStore(bus.subscribe, bus.getSnapshot, bus.getSnapshot);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(entry !== null);
  }, [entry]);

  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      // Closing via ESC / overlay click counts as a "cancel".
      bus.resolveCurrent(false);
    }
    setOpen(next);
  };

  if (!entry) return null;

  const tone: ConfirmTone = entry.tone ?? 'default';
  const title = entry.title ?? t('confirmModal.defaultTitle');
  const body = entry.body ?? t('confirmModal.defaultBody');
  const cancelLabel = entry.cancelLabel ?? tCommon('actions.cancel');
  const confirmLabel =
    entry.confirmLabel ??
    (tone === 'destructive' ? t('confirmModal.destructiveConfirm') : t('confirmModal.confirm'));

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="sm" data-testid="global-confirm-modal" data-tone={tone}>
        <DialogHeader title={title} onClose={() => handleOpenChange(false)} />
        <DialogBody>{body}</DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            size="md"
            onClick={() => {
              bus.resolveCurrent(false);
            }}
            data-testid="global-confirm-cancel"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'destructive' ? 'danger' : 'primary'}
            size="md"
            onClick={() => {
              bus.resolveCurrent(true);
            }}
            data-testid="global-confirm-accept"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
